import { useEffect, useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function ConfirmDialog({
  open, onOpenChange, title, description, confirmLabel = "Confirm", destructive, onConfirm,
  requireReason, reasonLabel = "Reason", minReason = 3, matchText, testId = "confirm-dialog",
}) {
  const [reason, setReason] = useState("");
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setReason("");
      setTyped("");
    }
  }, [open]);

  const reasonOk = !requireReason || reason.trim().length >= minReason;
  const matchOk = !matchText || typed.trim() === String(matchText).trim();

  const submit = async () => {
    setBusy(true);
    try {
      await onConfirm(reason, typed.trim());
      setReason("");
      setTyped("");
      onOpenChange(false);
    } catch {
      // The caller surfaces the error. Swallow it here so a failed confirm
      // does not become an uncaught promise rejection.
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid={testId} className="bg-white">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {requireReason ? (
          <div className="space-y-1.5">
            <Label htmlFor="confirm-reason">{reasonLabel}</Label>
            <Input
              id="confirm-reason"
              data-testid="confirm-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Describe why this action is needed"
            />
          </div>
        ) : null}
        {matchText ? (
          <div className="space-y-1.5">
            <Label htmlFor="confirm-match">Type {matchText} to confirm</Label>
            <Input
              id="confirm-match"
              data-testid="confirm-match-input"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={matchText}
              autoComplete="off"
            />
          </div>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid="confirm-accept"
            disabled={busy || !reasonOk || !matchOk}
            onClick={(e) => {
              e.preventDefault();
              submit();
            }}
            className={destructive ? "bg-rose-600 hover:bg-rose-700" : "bg-blue-600 hover:bg-blue-700"}
          >
            {busy ? "Working…" : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
